import { useEffect, useRef, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Card } from "@volca/ui/components/ui/card";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { getErrorMessage } from "@/core/api/errors";
import { PackChatAiStatus } from "@/payroll/pack-chat/pack-chat-ai-status";
import { PackChatRefreshError } from "@/payroll/pack-chat/pack-chat-refresh-error";
import { PackChatReplyForm } from "@/payroll/pack-chat/pack-chat-reply-form";
import { PackChatTranscriptSkeleton } from "@/payroll/pack-chat/pack-chat-skeletons";
import { PackChatState } from "@/payroll/pack-chat/pack-chat-state";
import { PackChatTranscript } from "@/payroll/pack-chat/pack-chat-transcript";
import {
  useGetPackThread,
  useReplyToPackThread,
  useRequestPackThreadManager,
} from "@/payroll/pack-chat/query";
import { isTemporaryPackChatError } from "@/payroll/pack-chat/thread-error-copy";
import { getPackChatThreadErrorCopy } from "@/payroll/pack-chat/thread-error-copy";
import type { IPackChatThreadSummary } from "@/payroll/pack-chat/types";
import { PackChatThreadHeader } from "./pack-chat-thread-header";

/** Owns the technician's conversation data, handoff, and replies. */
export const PackChatThreadItem = ({
  businessId,
  runId,
  thread: summary,
  expanded,
  onToggle,
  scrollToThread,
}: {
  businessId: string | null;
  runId: string;
  thread: IPackChatThreadSummary;
  expanded: boolean;
  onToggle: () => void;
  scrollToThread?: boolean;
}) => {
  const row = useRef<HTMLDivElement>(null);
  const [confirmingHandoff, setConfirmingHandoff] = useState(false);
  useEffect(() => {
    const item = row.current;
    const scroller = item?.closest<HTMLElement>("[data-pack-question-scroll]");
    if (scrollToThread && item && scroller) {
      scroller.scrollTop +=
        item.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
    }
  }, [scrollToThread]);

  const detailQuery = useGetPackThread(
    businessId,
    runId,
    expanded ? summary.id : null,
  );
  const reply = useReplyToPackThread(businessId, runId);
  const requestManager = useRequestPackThreadManager(businessId, runId);
  const thread = detailQuery.data?.thread ?? summary;
  const detail = detailQuery.data?.thread;
  const refreshFailed = Boolean(
    detail && isTemporaryPackChatError(detailQuery.error),
  );
  const handleRequestManager = async () => {
    try {
      await requestManager.mutateAsync(summary.id);
      setConfirmingHandoff(false);
      toast.success("Manager requested", {
        description:
          "Your manager can reply in this conversation. AI replies have stopped.",
      });
    } catch (error) {
      setConfirmingHandoff(false);
      toast.error("Could not request a manager", {
        description: getErrorMessage(error),
      });
    }
  };

  // A link, not a button: it rides the state row rather than earning a band of
  // its own below the composer, and it sits outside the transcript branch so it
  // stays reachable while an answer is generating and after one has failed.
  //
  // It asks first. The link lands about 17px above the send button with their
  // right edges nearly in line, and handing off cannot be taken back: AI never
  // returns to the conversation and a manager has already been emailed. The
  // confirm stays on this row, so changing your mind costs nothing and being
  // sure costs one more tap.
  const canRequestManager =
    thread.response_owner === "ai" && thread.status === "open";
  const requestManagerAction = !canRequestManager ? null : confirmingHandoff ? (
    <Stack
      direction="row"
      align="center"
      gap={2}
      className="ml-auto shrink-0 pl-2"
    >
      <Text variant="caption" tone="muted">
        Send to a manager?
      </Text>
      <Button
        variant="link"
        size="xs"
        aria-label="Yes, request a manager"
        className="h-auto px-0 py-0"
        aria-busy={requestManager.isPending || undefined}
        disabled={requestManager.isPending}
        onClick={() => void handleRequestManager()}
      >
        {requestManager.isPending && (
          <Spinner data-icon="inline-start" aria-hidden />
        )}
        Yes
      </Button>
      <Button
        variant="ghost"
        size="xs"
        aria-label="Keep this with AI"
        className="h-auto px-0 py-0"
        disabled={requestManager.isPending}
        onClick={() => setConfirmingHandoff(false)}
      >
        Cancel
      </Button>
    </Stack>
  ) : (
    <Button
      variant="link"
      size="xs"
      className="ml-auto h-auto px-0 py-0"
      onClick={() => setConfirmingHandoff(true)}
    >
      Request manager
    </Button>
  );

  return (
    <Card ref={row} className="w-full min-w-0 items-start gap-0 py-0">
      <PackChatThreadHeader
        thread={thread}
        expanded={expanded}
        onToggle={onToggle}
      />

      {expanded && (
        <Stack
          align="start"
          gap={2}
          className="w-full border-t border-line px-4 py-4"
        >
          {refreshFailed && (
            <PackChatRefreshError onRetry={() => void detailQuery.refetch()} />
          )}
          {detailQuery.isLoading ? (
            <PackChatTranscriptSkeleton rows={2} />
          ) : detailQuery.isError && !refreshFailed ? (
            <PackChatState
              error={detailQuery.error}
              {...getPackChatThreadErrorCopy(detailQuery.error)}
              layout="collection"
              action={
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={detailQuery.isFetching}
                  aria-busy={detailQuery.isFetching || undefined}
                  className="mt-2"
                  onClick={() => void detailQuery.refetch()}
                >
                  {detailQuery.isFetching && (
                    <Spinner data-icon="inline-start" aria-hidden />
                  )}
                  Try again
                </Button>
              }
            />
          ) : detail ? (
            /* No sourceAccess: a technician reads their own pack, so the answer
               does not cite it back to them. */
            <PackChatTranscript messages={detail.messages} />
          ) : null}

          <PackChatAiStatus thread={thread} action={requestManagerAction} />

          {detail && (
            <>
              <PackChatReplyForm
                loading={reply.isPending}
                reopens={detail.status === "closed"}
                onReply={(body) =>
                  reply
                    .mutateAsync({ threadId: thread.id, body })
                    .then(() => undefined)
                }
              />
              {detail.status === "closed" && (
                <Text variant="body" tone="muted">
                  {detail.closed_by
                    ? `Closed by ${detail.closed_by.name}. Replying reopens it.`
                    : "This thread is closed. Replying reopens it."}
                </Text>
              )}
            </>
          )}
        </Stack>
      )}
    </Card>
  );
};
